// Map view (PLAN §9, §12.7 screen 7; task 30): Places with coordinates and the Events held there, on a basemap
// served from R2. The API assembles the data through the claims service; everything here is pure and shared (the
// shapes, coordinates as written in the Place's *Coordinates* field, filters, and the features the map draws).

import type { EntitySummary, ReviewStatus } from './claims';
import type { GazetteerHit } from './gazetteer';
import { itemMatches, type TimelineItem } from './timeline';
import type { Sensitivity } from './uploads';

/** The Place field that holds coordinates ("Latitude, longitude", decimal degrees). */
export const COORDINATES_FIELD = 'coordinates';
/** Located places read per request; more are reported as `truncated`. */
export const MAP_MAX_PLACES = 5000;
/** Places without coordinates listed on the map page (most recently changed first). */
export const MAP_MAX_UNLOCATED = 200;
/** Gazetteer candidates offered when locating a place. */
export const PLACE_MATCH_CANDIDATES = 8;

type Status = Exclude<ReviewStatus, 'rejected'>;

export interface LatLng {
  lat: number;
  lng: number;
}

/** A Place with coordinates. */
export interface MapPlace extends LatLng {
  /** The Place entity. */
  id: string;
  /** The *Coordinates* claim the position comes from. */
  claimId: string;
  status: Status;
  sensitivity: Sensitivity;
}

/** An Event linked to a located Place (its *Place* field or a *held at* relation). One per event and place. */
export interface MapEvent extends Pick<TimelineItem, 'from' | 'to' | 'openEnd' | 'approximate' | 'precision' | 'dateText'> {
  /** `<event id>:<place id>`. */
  id: string;
  eventId: string;
  placeId: string;
  /** The claims that link the event to the place. */
  claimIds: string[];
  /** The least settled status among the links (disputed › unreviewed › verified). */
  status: Status;
  /** The most restrictive sensitivity among the links and the event. */
  sensitivity: Sensitivity;
}

export interface MapData {
  places: MapPlace[];
  events: MapEvent[];
  /** Summaries (with rules-mode styles) of every place and event above. */
  entities: EntitySummary[];
  /** Places without coordinates, most recently changed first (at most MAP_MAX_UNLOCATED). */
  unlocated: EntitySummary[];
  unlocatedTotal: number;
  /** Events linked only to places without coordinates. */
  eventsAtUnlocated: number;
  /** Events with no place at all. */
  eventsWithoutPlace: number;
  /** More located places matched than were read. */
  truncated: boolean;
  /** Whether the basemap (PMTiles on R2) is installed in this environment. */
  basemap: boolean;
}

const NUM = String.raw`[-+]?\d{1,3}(?:[.,]\d+)?`;
const PAIR = new RegExp(String.raw`^\s*(${NUM})\s*[,;\s]\s*(${NUM})\s*$`);

/**
 * Coordinates as people write them in the *Coordinates* field: "50.8467, 4.3525", "50.8467 4.3525", "50,8467; 4,3525".
 * Latitude first. Out-of-range values → null.
 */
export function parseCoordinates(v: unknown): LatLng | null {
  if (v && typeof v === 'object' && 'lat' in v && 'lng' in v) {
    const { lat, lng } = v as { lat: unknown; lng: unknown };
    return validLatLng(Number(lat), Number(lng));
  }
  if (typeof v !== 'string') return null;
  const m = PAIR.exec(v.replace(/[°]/g, ''));
  if (!m) return null;
  return validLatLng(Number(m[1]?.replace(',', '.')), Number(m[2]?.replace(',', '.')));
}

export function validLatLng(lat: number, lng: number): LatLng | null {
  if (!Number.isFinite(lat) || !Number.isFinite(lng) || Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
  return { lat, lng };
}

/** "48.85341, 2.3488": the value written to the *Coordinates* field (5 decimals ≈ 1 m). */
export const formatCoordinates = ({ lat, lng }: LatLng) => `${Number(lat.toFixed(5))}, ${Number(lng.toFixed(5))}`;

export const MAP_KINDS = ['place', 'event'] as const;
export type MapKind = (typeof MAP_KINDS)[number];
export const MAP_KIND_LABELS: Record<MapKind, string> = { place: 'Places', event: 'Events' };

export interface MapFilter {
  kinds?: MapKind[];
  statuses?: Status[];
  /** Events overlapping these years (inclusive). Undated events are hidden while a year is set. */
  fromYear?: number | null;
  toYear?: number | null;
}

export function eventMatches(e: MapEvent, f: MapFilter, now = Date.now()): boolean {
  if (f.kinds?.length && !f.kinds.includes('event')) return false;
  return itemMatches(
    { ...e, lane: '', kind: 'event', entityId: e.eventId, roles: [], text: null, category: null, dateSource: null, reliability: null },
    { statuses: f.statuses, fromYear: f.fromYear, toYear: f.toYear },
    now,
  );
}

export function placeMatches(p: MapPlace, f: MapFilter): boolean {
  if (f.kinds?.length && !f.kinds.includes('place')) return false;
  return !f.statuses?.length || f.statuses.includes(p.status);
}

/** One point the map draws: a Place, or an Event at its Place's position. */
export interface MapFeature extends LatLng {
  /** Place id, or MapEvent id. */
  id: string;
  kind: MapKind;
  entityId: string;
  placeId: string;
  status: Status;
  sensitivity: Sensitivity;
}

/** The points to draw under a filter. Events sit on their place; the map clusters points that overlap. */
export function mapFeatures(data: Pick<MapData, 'places' | 'events'>, f: MapFilter = {}, now = Date.now()): MapFeature[] {
  const at = new Map(data.places.map(p => [p.id, p]));
  const out: MapFeature[] = [];
  for (const p of data.places)
    if (placeMatches(p, f))
      out.push({
        id: p.id,
        kind: 'place',
        entityId: p.id,
        placeId: p.id,
        lat: p.lat,
        lng: p.lng,
        status: p.status,
        sensitivity: p.sensitivity,
      });
  for (const e of data.events) {
    const p = at.get(e.placeId);
    if (!p || !eventMatches(e, f, now)) continue;
    out.push({
      id: e.id,
      kind: 'event',
      entityId: e.eventId,
      placeId: p.id,
      lat: p.lat,
      lng: p.lng,
      status: e.status,
      sensitivity: e.sensitivity,
    });
  }
  return out;
}

/** [west, south, east, north] around the points, or null when there are none. */
export function featureBounds(points: readonly LatLng[]): [number, number, number, number] | null {
  if (!points.length) return null;
  let [w, s, e, n] = [180, 90, -180, -90];
  for (const p of points) {
    w = Math.min(w, p.lng);
    e = Math.max(e, p.lng);
    s = Math.min(s, p.lat);
    n = Math.max(n, p.lat);
  }
  return [w, s, e, n];
}

/** How a suggested match was chosen. */
export type PlaceMatchMethod = 'single' | 'context' | 'ai' | 'ranked' | 'none';

export const PLACE_MATCH_METHOD_LABELS: Record<PlaceMatchMethod, string> = {
  single: 'The only gazetteer entry with this name',
  context: 'Picked from the place’s country',
  ai: 'Picked by AI from the place’s context',
  ranked: 'The largest place with this name (AI not available)',
  none: 'No confident match',
};

/** Gazetteer candidates for a Place, with the one suggested (a person confirms before anything is written). */
export interface PlaceMatch {
  placeId: string;
  /** The names searched (the Place's names in every script). */
  query: string[];
  candidates: GazetteerHit[];
  suggested: number | null;
  method: PlaceMatchMethod;
  confidence: number | null;
  /** Why (AI answers say which context decided). */
  reason: string | null;
  /** The AI route used, when AI picked. */
  route: string | null;
}

/** Body of `POST /api/map/places/:id/locate`. */
export interface LocateInput extends LatLng {
  /** The gazetteer entry picked, if any (kept in the audit log); omitted for a pin set by hand. */
  geonameId?: number | null;
}
